/**
 * Push Notifications utilities for Capacitor native apps
 */

import { isCapacitorNative, getPlatform, getApiBaseUrl } from './capacitor';

// Type definitions for the Push Notifications plugin
interface PushNotificationToken {
  value: string;
}

interface PushNotificationSchema {
  title?: string;
  subtitle?: string;
  body?: string;
  id: string;
  badge?: number;
  notification?: unknown;
  data: Record<string, unknown>;
  click_action?: string;
  link?: string;
}

interface ActionPerformed {
  actionId: string;
  inputValue?: string;
  notification: PushNotificationSchema;
}

interface PermissionStatus {
  receive: 'prompt' | 'prompt-with-rationale' | 'granted' | 'denied';
}

interface PushNotificationsPlugin {
  checkPermissions(): Promise<PermissionStatus>;
  requestPermissions(): Promise<PermissionStatus>;
  register(): Promise<void>;
  getDeliveredNotifications(): Promise<{ notifications: PushNotificationSchema[] }>;
  removeAllDeliveredNotifications(): Promise<void>;
  addListener(
    eventName: 'registration',
    callback: (token: PushNotificationToken) => void
  ): Promise<{ remove: () => void }>;
  addListener(
    eventName: 'registrationError',
    callback: (error: { error: string }) => void
  ): Promise<{ remove: () => void }>;
  addListener(
    eventName: 'pushNotificationReceived',
    callback: (notification: PushNotificationSchema) => void
  ): Promise<{ remove: () => void }>;
  addListener(
    eventName: 'pushNotificationActionPerformed',
    callback: (action: ActionPerformed) => void
  ): Promise<{ remove: () => void }>;
}

// Device ID storage key
const DEVICE_ID_KEY = 'ping_device_id';

// Current FCM token (cached for logout)
let currentFcmToken: string | null = null;

/**
 * Get or create a unique device ID
 * This persists across app restarts and is used to identify this device
 */
function getOrCreateDeviceId(): string {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);
  
  if (!deviceId) {
    // Generate a random UUID-like ID
    deviceId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }
  
  return deviceId;
}

/**
 * Get the Push Notifications plugin
 */
function getPushNotificationsPlugin(): PushNotificationsPlugin | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Capacitor = (window as any).Capacitor;
  if (Capacitor?.Plugins?.PushNotifications) {
    return Capacitor.Plugins.PushNotifications as PushNotificationsPlugin;
  }
  return null;
}

/**
 * Register the device token with the server
 */
async function registerTokenWithServer(
  userId: string,
  token: string,
  deviceId: string
): Promise<boolean> {
  try {
    const baseUrl = getApiBaseUrl();
    const platform = getPlatform();
    
    const response = await fetch(`${baseUrl}/api/push/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId,
        token,
        deviceId,
        platform,
      }),
    });

    const data = await response.json();
    return data.success;
  } catch (error) {
    console.error('[PushNotifications] Failed to register token:', error);
    return false;
  }
}

/**
 * Unregister the device token from the server (e.g., on logout)
 */
export async function unregisterPushToken(): Promise<void> {
  if (!currentFcmToken) {
    return;
  }

  try {
    const baseUrl = getApiBaseUrl();
    await fetch(`${baseUrl}/api/push/unregister`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: currentFcmToken }),
    });
    console.log('[PushNotifications] Token unregistered');
  } catch (error) {
    console.error('[PushNotifications] Failed to unregister token:', error);
  } finally {
    currentFcmToken = null;
  }
}

/**
 * Initialize push notifications for the app.
 * Call this after the user is authenticated.
 * 
 * @param userId - The authenticated user's ID
 * @param onNotificationReceived - Optional callback when notification is received while app is open
 * @param onNotificationTapped - Optional callback when user taps a notification
 */
export async function initializePushNotifications(
  userId: string,
  onNotificationReceived?: (notification: PushNotificationSchema) => void,
  onNotificationTapped?: (notification: PushNotificationSchema) => void
): Promise<{ success: boolean; error?: string }> {
  if (!isCapacitorNative()) {
    console.log('[PushNotifications] Not native platform, skipping');
    return { success: false, error: 'Not a native platform' };
  }

  const PushNotifications = getPushNotificationsPlugin();
  if (!PushNotifications) {
    console.warn('[PushNotifications] Plugin not available');
    return { success: false, error: 'Plugin not available' };
  }

  const deviceId = getOrCreateDeviceId();
  console.log('[PushNotifications] Device ID:', deviceId);

  try {
    // Helper to add listener with Capacitor 6 compatibility
    const safeAddListener = (
      eventName: string,
      callback: (...args: unknown[]) => void
    ) => {
      try {
        const result = (PushNotifications as unknown as { addListener: (name: string, cb: (...args: unknown[]) => void) => unknown }).addListener(eventName, callback);
        // Handle both Promise and direct return value
        if (result && typeof (result as Promise<unknown>).then === 'function') {
          (result as Promise<unknown>).catch((err) => {
            console.error(`[PushNotifications] Failed to add ${eventName} listener:`, err);
          });
        }
      } catch (err) {
        console.error(`[PushNotifications] Error adding ${eventName} listener:`, err);
      }
    };

    // Set up listeners before registering
    
    // Handle successful registration - receive FCM token
    safeAddListener('registration', async (token: unknown) => {
      const t = token as PushNotificationToken;
      console.log('[PushNotifications] Token received:', t.value.substring(0, 20) + '...');
      currentFcmToken = t.value;
      
      // Register with our server
      const registered = await registerTokenWithServer(userId, t.value, deviceId);
      if (registered) {
        console.log('[PushNotifications] Token registered with server');
      } else {
        console.warn('[PushNotifications] Failed to register token with server');
      }
    });

    // Handle registration errors
    safeAddListener('registrationError', (error: unknown) => {
      const err = error as { error: string };
      console.error('[PushNotifications] Registration error:', err.error);
    });

    // Handle notification received while app is in foreground
    safeAddListener('pushNotificationReceived', (notification: unknown) => {
      console.log('[PushNotifications] Notification received:', notification);
      if (onNotificationReceived) {
        onNotificationReceived(notification as PushNotificationSchema);
      }
    });

    // Handle notification tap (app opened via notification)
    safeAddListener('pushNotificationActionPerformed', (action: unknown) => {
      console.log('[PushNotifications] Notification tapped:', action);
      if (onNotificationTapped) {
        onNotificationTapped((action as ActionPerformed).notification);
      }
    });

    // Check current permission status
    let permStatus = await PushNotifications.checkPermissions();
    console.log('[PushNotifications] Current permission status:', permStatus.receive);

    // Request permission if needed (required for Android 13+)
    if (permStatus.receive === 'prompt' || permStatus.receive === 'prompt-with-rationale') {
      console.log('[PushNotifications] Requesting permission...');
      permStatus = await PushNotifications.requestPermissions();
      console.log('[PushNotifications] Permission response:', permStatus.receive);
    }

    // Only register if permission was granted
    if (permStatus.receive !== 'granted') {
      console.log('[PushNotifications] Permission not granted, skipping registration');
      return { success: false, error: 'Permission denied' };
    }

    // Register for push notifications
    await PushNotifications.register();
    console.log('[PushNotifications] Registration initiated');

    return { success: true };
  } catch (error) {
    console.error('[PushNotifications] Initialization failed:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

/**
 * Clear all delivered notifications from the notification tray
 */
export async function clearAllNotifications(): Promise<void> {
  if (!isCapacitorNative()) {
    return;
  }

  const PushNotifications = getPushNotificationsPlugin();
  if (PushNotifications) {
    try {
      await PushNotifications.removeAllDeliveredNotifications();
    } catch (error) {
      console.error('[PushNotifications] Failed to clear notifications:', error);
    }
  }
}
